import {DataType} from "../model/DataType.model";
import {GridIdKeyConstant, GridLinkIdKeyConstant} from "../component_element/manifest_constant/Grid.constant";
import {BuiltInGridType, GridSystemEntityName} from "./FormDataSchema.model";


export enum FilterMatchMode {
    EQUALS = "equals",
    NOTEQUALS = "notEquals",
    STARTSWITH = "startsWith",
    CONTAINS = "contains",
    ENDSWITH = "endsWith",
    BETWEEN = "between",
    IN = "in",
    SMALLER = "smallerThan",
    BIGGER = "biggerThan",
    SMALLEQUAL = "smallerOrEqual",
    BIGEQUAL = "biggerOrEqual",
    HASVALUE = "hasValue"
}

export enum FilterType {
    NUMBER = "number",
    NUMBERARRAY = "numberArray",
    DATE = "date",
    DATEARRAY = "dateArray",
    TIME = "time",
    STRING = "string",
    STRINGARRAY = "stringArray",
    LOOKUP = "lookup",
    LOOKUPARRAY = "lookupArray",
    USER = "user",
    FILE = "file",
    SIGN = "sign",
    ANY = "any"
}

export const FilterTypeMatchMode = {
    [FilterType.NUMBER]: [FilterMatchMode.BETWEEN, FilterMatchMode.EQUALS, FilterMatchMode.NOTEQUALS, FilterMatchMode.SMALLER, FilterMatchMode.BIGGER, FilterMatchMode.SMALLEQUAL, FilterMatchMode.BIGEQUAL, FilterMatchMode.HASVALUE],
    [FilterType.NUMBERARRAY]: [FilterMatchMode.BETWEEN, FilterMatchMode.IN, FilterMatchMode.SMALLER, FilterMatchMode.BIGGER, FilterMatchMode.SMALLEQUAL, FilterMatchMode.BIGEQUAL, FilterMatchMode.NOTEQUALS, FilterMatchMode.HASVALUE],
    [FilterType.DATE]: [FilterMatchMode.BETWEEN, FilterMatchMode.EQUALS, FilterMatchMode.SMALLER, FilterMatchMode.BIGGER, FilterMatchMode.SMALLEQUAL, FilterMatchMode.BIGEQUAL, FilterMatchMode.NOTEQUALS, FilterMatchMode.HASVALUE],
    [FilterType.DATEARRAY]: [FilterMatchMode.BETWEEN, FilterMatchMode.IN, FilterMatchMode.SMALLER, FilterMatchMode.BIGGER, FilterMatchMode.SMALLEQUAL, FilterMatchMode.BIGEQUAL, FilterMatchMode.NOTEQUALS, FilterMatchMode.HASVALUE],
    [FilterType.TIME]: [FilterMatchMode.BETWEEN, FilterMatchMode.EQUALS, FilterMatchMode.SMALLER, FilterMatchMode.BIGGER, FilterMatchMode.SMALLEQUAL, FilterMatchMode.BIGEQUAL, FilterMatchMode.NOTEQUALS, FilterMatchMode.HASVALUE],
    [FilterType.STRING]: [FilterMatchMode.CONTAINS, FilterMatchMode.STARTSWITH, FilterMatchMode.ENDSWITH, FilterMatchMode.EQUALS, FilterMatchMode.HASVALUE],
    [FilterType.STRINGARRAY]: [FilterMatchMode.CONTAINS, FilterMatchMode.STARTSWITH, FilterMatchMode.ENDSWITH, FilterMatchMode.IN, FilterMatchMode.HASVALUE],
    [FilterType.LOOKUP]: [FilterMatchMode.EQUALS, FilterMatchMode.IN, FilterMatchMode.STARTSWITH, FilterMatchMode.ENDSWITH, FilterMatchMode.HASVALUE],
    [FilterType.LOOKUPARRAY]: [FilterMatchMode.IN, FilterMatchMode.CONTAINS, FilterMatchMode.HASVALUE],
    [FilterType.USER]: [FilterMatchMode.CONTAINS, FilterMatchMode.STARTSWITH, FilterMatchMode.ENDSWITH, FilterMatchMode.EQUALS, FilterMatchMode.HASVALUE],
    [FilterType.FILE]: [FilterMatchMode.CONTAINS, FilterMatchMode.STARTSWITH, FilterMatchMode.ENDSWITH, FilterMatchMode.EQUALS, FilterMatchMode.HASVALUE],
    [FilterType.SIGN]: [FilterMatchMode.HASVALUE],
    [FilterType.ANY]: [ FilterMatchMode.CONTAINS, FilterMatchMode.EQUALS, FilterMatchMode.STARTSWITH, FilterMatchMode.ENDSWITH, FilterMatchMode.HASVALUE]
};

export const FilterTypeDataType = {
    [DataType.ANY]: FilterType.ANY,
    [DataType.BOOLEAN]: FilterType.STRING,
    [DataType.DATE]: FilterType.DATE,
    [DataType.DATEARRAY]: FilterType.DATEARRAY,
    [DataType.DATETIME]: FilterType.DATE,
    [DataType.UTCDATE]: FilterType.DATE,
    [DataType.UTCDATEARRAY]: FilterType.DATEARRAY,
    [DataType.UTCDATETIME]: FilterType.DATE,
    [DataType.INTEGER]: FilterType.NUMBER,
    [DataType.INTEGERARRAY]: FilterType.NUMBERARRAY,
    [DataType.LOOKUP]: FilterType.LOOKUP,
    [DataType.LOOKUPARRAY]: FilterType.LOOKUPARRAY,
    [DataType.NUMBER]: FilterType.NUMBER,
    [DataType.NUMBERARRAY]: FilterType.NUMBERARRAY,
    [DataType.STRING]: FilterType.STRING,
    [DataType.STRINGARRAY]: FilterType.STRINGARRAY,
    [DataType.TIME]: FilterType.TIME,
    [DataType.UTCTIME]: FilterType.TIME,
    [DataType.TRISTATE]: FilterType.STRING,
    [DataType.FILE]: FilterType.FILE,
    [DataType.SIGN]: FilterType.SIGN,
    [DataType.USER]: FilterType.USER
};

export const FilterDataTypeMatchMode = {
    [DataType.BOOLEAN]: [FilterMatchMode.EQUALS, FilterMatchMode.NOTEQUALS, FilterMatchMode.HASVALUE],
    [DataType.TRISTATE]: [FilterMatchMode.EQUALS, FilterMatchMode.NOTEQUALS, FilterMatchMode.HASVALUE]
};

export enum GridColumnWidthUint {
    PX = "px",
    PERCENT = "%",
    ASTERISK = "*"
}

export enum GridContextAndClickType {
    GLOBAL = "global",
    SELF = "self",
    NONE = "none"
}

export enum GridSpecialColumn {
    ACTION = "Action",
    CHECKBOX = "CheckBox"
}

export interface GridContextMenu {
    label?: string;
    confirm?: string;
    icon?: Array<string>;
    action?: string;
    state?: string;
    reload?: boolean;
}

export interface GridRepeatedFiltersInformation {
    entityId?: string;
    type?: DataType;
    use?: boolean;
    label?: string;
    placeholder?: string;
    filterFunc?: string;
    options?: Array<string>;
    format?: {
        input?: string;
        view?: string;
    };
}

export interface GridRepeatedColumnsInformation {
    entityId?: string;
    context?: Array<GridContextMenu>;
    click?: string;
    contextScope?: GridContextAndClickType;
    clickScope?: GridContextAndClickType;
    state?: string;
}

export interface GridTableColumnsInformation extends GridRepeatedColumnsInformation {
    label?: string;
    type?: DataType;
    special?: GridSpecialColumn;
    format?: any;
    usable?: boolean;
    defaultSort?: boolean;
    sortable?: boolean;
    editable?: boolean;
    reorderable?: boolean;
    frozen?: boolean;
    filterable?: boolean;
    filterHolder?: string;
    filterFunc?: string;
    filterOptions?: Array<FilterMatchMode>;
    unit?: GridColumnWidthUint;
    width?: number;
    template?: string;
}

export interface GridSearch {
    elementId?: string;
    entityId?: string;
    type?: DataType;
    filterMatchMode?: FilterMatchMode;
    isStartDate?: boolean;
    isEndDate?: boolean;
}

export enum GridContainerRole {
    "SEARCH" = "search",
    "ACTION" = "action",
    "BODY" = "body",
    "DIRECT" = "direct",
    "DETAIL" = "detail"
}

export type GridContainerInfo = {
    [GridIdKeyConstant]?: string;
    [GridLinkIdKeyConstant]?: string;
    containerElementId?: string;
    containerRole?: GridContainerRole;
    containerPk?: string;
    component?: string;
    rootDom?: HTMLElement;
    containerRootId?: string;
    from?: string;
};

export type GridProps = {
    [GridIdKeyConstant]?: string;
    [GridLinkIdKeyConstant]?: string;
    pk?: string;
    dataField?: string;
    containerElementId?: string;
    containerRole?: GridContainerRole;
    from?: string;
    isLookup?: boolean;
    forced?: boolean;
};

export const GridSystemEntity = {
    [BuiltInGridType.PK]: GridSystemEntityName.PK,
    [BuiltInGridType.EPK]: GridSystemEntityName.EPK,
    [BuiltInGridType.CUSER]: GridSystemEntityName.CUSER,
    [BuiltInGridType.LUSER]: GridSystemEntityName.LUSER,
    [BuiltInGridType.CDATE]: GridSystemEntityName.CDATE,
    [BuiltInGridType.LDATE]: GridSystemEntityName.LDATE
};

export type GridValidationForCompleteAction = {
    returnType?: string;
    title?: string;
    errorMsg?: string;
    clickMsg?: string;
    action?: string;
    state?: any;
};

export type GridValidationForComplete = {
    script?: string;
    action?: Array<GridValidationForCompleteAction>;
};
